import { CoreTypes, type ConnectorEnd, type Id, type LinkRecord, type ObjectRecord } from '@vp/shared-types';
import type { CanvasDocument, TxContext } from './document';
import { keyBetween, keysBetween } from './fractional';
import { boxBounds, unionRects, type Rect } from './geometry';
import { makeObject } from './objects';
import { ulid } from './ulid';

/** Shared editing operations used by the UI, modules and future MCP adapter (§5.3). */

export function compareZ(a: ObjectRecord, b: ObjectRecord): number {
  return a.z < b.z ? -1 : a.z > b.z ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function sortedByZ(doc: CanvasDocument): ObjectRecord[] {
  return [...doc.allObjects()].sort(compareZ);
}

export function topKey(doc: CanvasDocument): string | null {
  return doc.topZ();
}

/** All descendants (groups may nest). */
export function descendants(doc: CanvasDocument, id: Id, out: Id[] = []): Id[] {
  for (const c of doc.childrenOf(id)) {
    out.push(c);
    descendants(doc, c, out);
  }
  return out;
}

/** Ids plus their descendants, deduplicated. */
export function withDescendants(doc: CanvasDocument, ids: Iterable<Id>): Id[] {
  const set = new Set<Id>();
  for (const id of ids) {
    if (!doc.has(id)) continue;
    set.add(id);
    for (const d of descendants(doc, id)) set.add(d);
  }
  return [...set];
}

/**
 * Ids plus everything they contain: group descendants and, for frames, their
 * contents (recursively, including nested frames).
 */
export function withContents(doc: CanvasDocument, ids: Iterable<Id>): Id[] {
  const set = new Set<Id>();
  const stack = [...ids];
  while (stack.length) {
    const id = stack.pop()!;
    if (set.has(id) || !doc.has(id)) continue;
    set.add(id);
    for (const c of doc.childrenOf(id)) stack.push(c);
    for (const m of doc.membersOf(id)) stack.push(m);
  }
  return [...set];
}

/** Outermost group ancestor (for click selection). */
export function topAncestor(doc: CanvasDocument, id: Id): Id {
  let cur = doc.get(id);
  while (cur && cur.parentId) {
    const p = doc.get(cur.parentId);
    if (!p) break;
    cur = p;
  }
  return cur ? cur.id : id;
}

/** World-space bounds of an object (groups: union of children). */
export function objectBounds(doc: CanvasDocument, o: ObjectRecord): Rect {
  return boxBounds(o);
}

export function selectionBounds(doc: CanvasDocument, ids: Iterable<Id>): Rect | null {
  const rects: Rect[] = [];
  for (const id of ids) {
    const o = doc.get(id);
    if (o) rects.push(objectBounds(doc, o));
  }
  return unionRects(rects);
}

/** Recompute group boxes bottom-up after children changed. */
export function updateGroupBounds(tx: TxContext, groupId: Id): void {
  const doc = tx.document;
  const g = doc.get(groupId);
  if (!g || g.type !== CoreTypes.group) return;
  const kids = doc.childrenOf(groupId).map((id) => doc.get(id)!).filter(Boolean);
  if (kids.length === 0) {
    tx.delete(groupId);
  } else {
    const r = unionRects(kids.map((k) => boxBounds(k)))!;
    if (r.x !== g.x || r.y !== g.y || r.w !== g.w || r.h !== g.h || g.rotation !== 0) {
      tx.update(groupId, { x: r.x, y: r.y, w: r.w, h: r.h, rotation: 0 });
    }
  }
  if (g.parentId) updateGroupBounds(tx, g.parentId);
}

export function moveBy(tx: TxContext, ids: Iterable<Id>, dx: number, dy: number): void {
  const doc = tx.document;
  const all = withContents(doc, ids);
  const allSet = new Set(all);
  const parents = new Set<Id>();
  for (const id of all) {
    const o = doc.get(id)!;
    tx.update(id, { x: o.x + dx, y: o.y + dy });
    if (o.parentId && !allSet.has(o.parentId)) parents.add(o.parentId);
  }
  for (const p of parents) updateGroupBounds(tx, p);
}

export function deleteDeep(tx: TxContext, ids: Iterable<Id>): void {
  const doc = tx.document;
  const all = withContents(doc, ids);
  const allSet = new Set(all);
  const parents = new Set<Id>();
  for (const id of all) {
    const p = doc.get(id)?.parentId;
    if (p && !allSet.has(p)) parents.add(p);
  }
  // detach connectors bound to deleted objects
  const deleted = new Set(all);
  for (const o of doc.allObjects()) {
    if (o.type !== CoreTypes.connector || deleted.has(o.id)) continue;
    const s = o.props['start'] as ConnectorEnd | undefined;
    const e = o.props['end'] as ConnectorEnd | undefined;
    const patch: Record<string, unknown> = {};
    if (s?.objectId && deleted.has(s.objectId)) patch['start'] = { point: s.point };
    if (e?.objectId && deleted.has(e.objectId)) patch['end'] = { point: e.point };
    if (Object.keys(patch).length) tx.updateProps(o.id, patch);
  }
  for (const id of all) tx.delete(id);
  for (const p of parents) if (doc.has(p)) updateGroupBounds(tx, p);
}

export function setLocked(tx: TxContext, ids: Iterable<Id>, locked: boolean): void {
  for (const id of withDescendants(tx.document, ids)) {
    const o = tx.get(id);
    if (o && o.locked !== locked) tx.update(id, { locked });
  }
}

export type ReorderMode = 'front' | 'back' | 'forward' | 'backward';

/** Change layer order; keeps relative order of the moved objects. */
export function reorder(tx: TxContext, ids: Iterable<Id>, mode: ReorderMode): void {
  const doc = tx.document;
  const moving = new Set(withContents(doc, ids));
  if (moving.size === 0) return;
  const sorted = sortedByZ(doc);
  const movingSorted = sorted.filter((o) => moving.has(o.id));
  const rest = sorted.filter((o) => !moving.has(o.id));
  let lo: string | null;
  let hi: string | null;
  if (mode === 'front') {
    lo = rest.length ? rest[rest.length - 1].z : null;
    hi = null;
  } else if (mode === 'back') {
    lo = null;
    hi = rest.length ? rest[0].z : null;
  } else {
    const firstIdx = sorted.findIndex((o) => moving.has(o.id));
    let lastIdx = -1;
    for (let i = sorted.length - 1; i >= 0; i--) if (moving.has(sorted[i].id)) { lastIdx = i; break; }
    if (mode === 'forward') {
      // place after the first non-moving object above the topmost moving one
      let j = lastIdx + 1;
      while (j < sorted.length && moving.has(sorted[j].id)) j++;
      if (j >= sorted.length) return;
      lo = sorted[j].z;
      let k = j + 1;
      while (k < sorted.length && moving.has(sorted[k].id)) k++;
      hi = k < sorted.length ? sorted[k].z : null;
    } else {
      let j = firstIdx - 1;
      while (j >= 0 && moving.has(sorted[j].id)) j--;
      if (j < 0) return;
      hi = sorted[j].z;
      let k = j - 1;
      while (k >= 0 && moving.has(sorted[k].id)) k--;
      lo = k >= 0 ? sorted[k].z : null;
    }
  }
  if (lo !== null && hi !== null && lo >= hi) hi = null; // equal keys: fall back to open end
  const keys = keysBetween(lo, hi, movingSorted.length);
  movingSorted.forEach((o, i) => tx.update(o.id, { z: keys[i] }));
}

export function group(tx: TxContext, ids: Id[]): Id | null {
  const doc = tx.document;
  const roots = [...new Set(ids.map((id) => topAncestor(doc, id)))].filter((id) => doc.has(id));
  if (roots.length < 2) return null;
  const members = roots.map((id) => doc.get(id)!);
  const r = unionRects(members.map((m) => boxBounds(m)))!;
  const commonParent = members.every((m) => m.parentId === members[0].parentId) ? members[0].parentId : null;
  const maxZ = members.reduce((a, m) => (m.z > a ? m.z : a), members[0].z);
  const g = makeObject(doc.canvasId, { type: CoreTypes.group, x: r.x, y: r.y, w: r.w, h: r.h, z: maxZ, parentId: commonParent });
  tx.create(g);
  for (const m of members) tx.update(m.id, { parentId: g.id });
  if (commonParent) updateGroupBounds(tx, commonParent);
  return g.id;
}

export function ungroup(tx: TxContext, groupIds: Id[]): Id[] {
  const doc = tx.document;
  const freed: Id[] = [];
  for (const gid of groupIds) {
    const g = doc.get(gid);
    if (!g || g.type !== CoreTypes.group) continue;
    for (const c of doc.childrenOf(gid)) {
      tx.update(c, { parentId: g.parentId });
      freed.push(c);
    }
    tx.delete(gid);
    if (g.parentId) updateGroupBounds(tx, g.parentId);
  }
  return freed;
}

export type AlignMode = 'left' | 'centerX' | 'right' | 'top' | 'centerY' | 'bottom' | 'distributeX' | 'distributeY';

export function align(tx: TxContext, ids: Id[], mode: AlignMode): void {
  const doc = tx.document;
  const items = [...new Set(ids.map((id) => topAncestor(doc, id)))]
    .map((id) => doc.get(id))
    .filter((o): o is ObjectRecord => !!o && !o.locked);
  if (items.length < 2) return;
  const bounds = items.map((o) => ({ o, b: boxBounds(o) }));
  const all = unionRects(bounds.map((x) => x.b))!;
  if (mode === 'distributeX' || mode === 'distributeY') {
    if (items.length < 3) return;
    const horiz = mode === 'distributeX';
    bounds.sort((a, b) => (horiz ? a.b.x - b.b.x : a.b.y - b.b.y));
    const total = bounds.reduce((s, x) => s + (horiz ? x.b.w : x.b.h), 0);
    const gap = ((horiz ? all.w : all.h) - total) / (bounds.length - 1);
    let pos = horiz ? all.x : all.y;
    for (const { o, b } of bounds) {
      const d = pos - (horiz ? b.x : b.y);
      if (d !== 0) moveBy(tx, [o.id], horiz ? d : 0, horiz ? 0 : d);
      pos += (horiz ? b.w : b.h) + gap;
    }
    return;
  }
  for (const { o, b } of bounds) {
    let dx = 0;
    let dy = 0;
    switch (mode) {
      case 'left': dx = all.x - b.x; break;
      case 'right': dx = all.x + all.w - (b.x + b.w); break;
      case 'centerX': dx = all.x + all.w / 2 - (b.x + b.w / 2); break;
      case 'top': dy = all.y - b.y; break;
      case 'bottom': dy = all.y + all.h - (b.y + b.h); break;
      case 'centerY': dy = all.y + all.h / 2 - (b.y + b.h / 2); break;
    }
    if (dx || dy) moveBy(tx, [o.id], dx, dy);
  }
}

// ---------- copy / paste / duplicate (§4.6) ----------

export interface ClipPayload {
  format: 'vp-clip';
  version: 1;
  objects: ObjectRecord[];
  links: LinkRecord[];
  /** Bounds of copied set for placement. */
  bounds: Rect;
}

export function serializeSelection(doc: CanvasDocument, ids: Iterable<Id>): ClipPayload | null {
  const all = withContents(doc, ids);
  if (all.length === 0) return null;
  const set = new Set(all);
  const objects = all.map((id) => structuredClone(doc.get(id)!)).sort(compareZ);
  // keep only links whose both ends are copied
  const links: LinkRecord[] = [];
  for (const l of doc.allLinks()) if (set.has(l.from) && set.has(l.to)) links.push(structuredClone(l));
  const roots = objects.filter((o) => !o.parentId || !set.has(o.parentId));
  const bounds = unionRects(roots.map((o) => boxBounds(o)))!;
  return { format: 'vp-clip', version: 1, objects, links, bounds };
}

/**
 * Instantiate a payload into `tx` with fresh ids, translated so that its
 * bounds' top-left lands at `at`. Returns new root ids.
 */
export function instantiatePayload(tx: TxContext, payload: ClipPayload, at: { x: number; y: number }): Id[] {
  const doc = tx.document;
  const idMap = new Map<Id, Id>();
  for (const o of payload.objects) idMap.set(o.id, ulid());
  const dx = at.x - payload.bounds.x;
  const dy = at.y - payload.bounds.y;
  const keys = keysBetween(topKey(doc), null, payload.objects.length);
  const roots: Id[] = [];
  payload.objects.forEach((o, i) => {
    const parentId = o.parentId && idMap.has(o.parentId) ? idMap.get(o.parentId)! : null;
    const props = structuredClone(o.props);
    if (o.type === CoreTypes.connector) {
      for (const end of ['start', 'end'] as const) {
        const e = props[end] as ConnectorEnd | undefined;
        if (!e) continue;
        const target = e.objectId ? idMap.get(e.objectId) : undefined;
        props[end] = target
          ? { ...e, objectId: target, point: { x: e.point.x + dx, y: e.point.y + dy } }
          : { point: { x: e.point.x + dx, y: e.point.y + dy } };
      }
    }
    const frameId = o.frameId && idMap.has(o.frameId) ? idMap.get(o.frameId)! : undefined;
    const rec: ObjectRecord = {
      ...structuredClone(o),
      id: idMap.get(o.id)!,
      canvasId: doc.canvasId,
      parentId,
      frameId,
      z: keys[i],
      x: o.x + dx,
      y: o.y + dy,
      locked: false,
      props,
    };
    tx.create(rec);
    if (!parentId && !frameId) roots.push(rec.id);
  });
  for (const l of payload.links) {
    const from = idMap.get(l.from);
    const to = idMap.get(l.to);
    if (!from || !to) continue;
    tx.createLink({ ...structuredClone(l), id: ulid(), canvasId: doc.canvasId, from, to });
  }
  return roots;
}

export function duplicate(tx: TxContext, ids: Id[], offset = 20): Id[] {
  const payload = serializeSelection(tx.document, ids);
  if (!payload) return [];
  return instantiatePayload(tx, payload, { x: payload.bounds.x + offset, y: payload.bounds.y + offset });
}

export function nextZ(doc: CanvasDocument): string {
  return keyBetween(topKey(doc), null);
}

// ---------- frames: full containers ----------

function frameRect(o: ObjectRecord): Rect {
  return boxBounds(o);
}

function containsPoint(r: Rect, x: number, y: number): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

function containsRect(outer: Rect, inner: Rect): boolean {
  return inner.x >= outer.x && inner.y >= outer.y && inner.x + inner.w <= outer.x + outer.w && inner.y + inner.h <= outer.y + outer.h;
}

/** Smallest key strictly above `z` among existing objects, for inserting right above it. */
function keyRightAbove(doc: CanvasDocument, z: string): string {
  let next: string | null = null;
  for (const o of doc.allObjects()) if (o.z > z && (next === null || o.z < next)) next = o.z;
  try {
    return keyBetween(z, next);
  } catch {
    // malformed keys from foreign data: fall back to the top of the canvas
    return nextZ(doc);
  }
}

/** Keep frame contents above the frame in layer order (recursively for nested frames). */
export function ensureAboveFrame(tx: TxContext, id: Id, frameId: Id): void {
  const doc = tx.document;
  const f = doc.get(frameId);
  const o = doc.get(id);
  if (!f || !o) return;
  if (o.z <= f.z) {
    tx.update(id, { z: keyRightAbove(doc, f.z) });
    for (const c of doc.childrenOf(id)) ensureAboveFrame(tx, c, frameId);
  }
  for (const m of doc.membersOf(id)) ensureAboveFrame(tx, m, id);
}

/**
 * Put objects into the frame under their center (topmost frame wins), or take them
 * out of frames they left. Group members follow their group root.
 */
export function assignFrames(tx: TxContext, ids: Iterable<Id>): void {
  const doc = tx.document;
  const frames = [...doc.allObjects()].filter((o) => o.type === CoreTypes.frame).sort((a, b) => (a.z < b.z ? 1 : -1));
  const seen = new Set<Id>();
  for (const raw of ids) {
    const id = topAncestor(doc, raw);
    if (seen.has(id)) continue;
    seen.add(id);
    const o = doc.get(id);
    if (!o || o.type === CoreTypes.connector) continue;
    // a frame cannot go into itself or into frames it contains
    const exclude = new Set(o.type === CoreTypes.frame ? withContents(doc, [id]) : [id]);
    const b = boxBounds(o);
    const cx = b.x + b.w / 2;
    const cy = b.y + b.h / 2;
    const target = frames.find((f) => !exclude.has(f.id) && containsPoint(frameRect(f), cx, cy)) ?? null;
    const next = target?.id ?? null;
    if ((o.frameId ?? null) !== next) tx.update(id, { frameId: next ?? undefined } as Partial<ObjectRecord>);
    if (next) ensureAboveFrame(tx, id, next);
  }
}

/** A new or resized frame takes objects that lie fully inside it (at the same nesting level). */
/** `only`: adopt just these objects (a module that may change only its own takes no one else's). */
export function adoptIntoFrame(tx: TxContext, frameId: Id, only?: ReadonlySet<Id>): Id[] {
  const doc = tx.document;
  const f = doc.get(frameId);
  if (!f || f.type !== CoreTypes.frame) return [];
  const r = frameRect(f);
  const level = f.frameId ?? null;
  const own = new Set(withContents(doc, [frameId]));
  const adopted: Id[] = [];
  for (const o of doc.allObjects()) {
    if (own.has(o.id) || o.parentId || o.type === CoreTypes.connector || (only && !only.has(o.id))) continue;
    if ((o.frameId ?? null) !== level) continue;
    if (!containsRect(r, boxBounds(o))) continue;
    tx.update(o.id, { frameId } as Partial<ObjectRecord>);
    adopted.push(o.id);
  }
  // the frame sits below what it contains
  for (const id of adopted) ensureAboveFrame(tx, id, frameId);
  return adopted;
}

/** Remove a frame but keep its contents (they move up one nesting level). */
export function unframe(tx: TxContext, frameId: Id): Id[] {
  const doc = tx.document;
  const f = doc.get(frameId);
  if (!f || f.type !== CoreTypes.frame) return [];
  const freed = doc.membersOf(frameId);
  for (const id of freed) tx.update(id, { frameId: f.frameId ?? undefined } as Partial<ObjectRecord>);
  tx.delete(frameId);
  return freed;
}

/** Frame that directly contains an object (null at top level). */
export function frameOf(doc: CanvasDocument, id: Id): Id | null {
  return doc.get(topAncestor(doc, id))?.frameId ?? null;
}
